import { useMutation, useQuery } from "react-query";
import HttpService from "../../services/http";
import useAuth from "../useAuth";
import SecureStorage from "../../utils/secureStorage";
import { queryClient } from "../../providers/QueryProvider";
import { RoleMatrix, RbacLevel } from "../../utils/rbacCatalog";

interface MyPermissionsResponse {
  role: string | null;
  governed: boolean;
  permissions: RoleMatrix | null;
}

const basePath = (userId: string, entityId: string) =>
  `/${userId}/${entityId}/role`;

export const useHydrateMyPermissions = () => {
  const { user, setUser } = useAuth();

  const enabled =
    user?.isAuthenticated === true &&
    Boolean(user?.user_identification) &&
    Boolean(user?.entity_identification);

  const query = useQuery<MyPermissionsResponse>(
    ["my-permissions", user?.user_identification, user?.entity_identification],
    async () => {
      const res = await HttpService.get(
        `${basePath(user.user_identification, user.entity_identification)}/me`
      );
      return res?.data?.element as MyPermissionsResponse;
    },
    {
      enabled,
      staleTime: 60 * 1000,
      retry: 1,
      onSuccess: (data) => {
        const rolePermissions = data?.governed ? data?.permissions ?? {} : null;
        if (
          JSON.stringify(user.role_permissions ?? null) ===
          JSON.stringify(rolePermissions)
        ) {
          return;
        }
        const nextUser = { ...user, role_permissions: rolePermissions };
        setUser(nextUser);
        try {
          SecureStorage.updateAuthData({ role_permissions: rolePermissions });
        } catch {}
      },
      onError: () => {
        setUser({ ...user, role_permissions: null });
      },
    }
  );

  return query;
};

export interface CatalogGroup {
  group: string;
  capabilities: Array<{ key: string; label: string; maxLevel: RbacLevel }>;
}
export interface CatalogPreset {
  key: string;
  label: string;
  matrix: RoleMatrix;
}
export interface RolesCatalog {
  groups: CatalogGroup[];
  presets: CatalogPreset[];
}

export const useRolesCatalog = (enabled = true) => {
  const { user } = useAuth();
  return useQuery<RolesCatalog>(
    ["roles-catalog", user?.entity_identification],
    async () => {
      const res = await HttpService.get(
        `${basePath(
          user.user_identification,
          user.entity_identification
        )}/catalog`
      );
      return res?.data?.element as RolesCatalog;
    },
    {
      enabled: enabled && Boolean(user?.entity_identification),
      staleTime: 60 * 60 * 1000,
    }
  );
};

export const useDashboardAccessByRole = () => {
  const { user } = useAuth();
  return useQuery<Record<string, boolean>>(
    ["dashboard-access-by-role", user?.entity_identification],
    async () => {
      const res = await HttpService.get(
        `${basePath(
          user.user_identification,
          user.entity_identification
        )}/dashboard-access`
      );
      return (res?.data?.element?.access ?? {}) as Record<string, boolean>;
    },
    { enabled: Boolean(user?.entity_identification), staleTime: 10 * 60 * 1000 }
  );
};

export const isTeamMemberRole = (
  role: string | undefined | null,
  accessByRole: Record<string, boolean> | undefined
): boolean => {
  if (!role || !accessByRole) return false;
  return accessByRole[role.toUpperCase()] === true;
};

export const useRolesList = (enabled = true) => {
  const { user } = useAuth();
  return useQuery(
    ["roles-list", user?.entity_identification],
    async () => {
      const res = await HttpService.get(
        `${basePath(user.user_identification, user.entity_identification)}/`
      );
      return res?.data?.element as Array<{
        role: string;
        permissions: RoleMatrix;
      }>;
    },
    { enabled: enabled && Boolean(user?.entity_identification) }
  );
};

export const useUpsertRole = () => {
  const { user } = useAuth();
  return useMutation(
    async ({
      role,
      permissions,
    }: {
      role: string;
      permissions: RoleMatrix;
    }) => {
      const res = await HttpService.put(
        `${basePath(
          user.user_identification,
          user.entity_identification
        )}/${encodeURIComponent(role)}`,
        { permissions }
      );
      return res?.data?.element;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "roles-list",
          user?.entity_identification,
        ]);
      },
    }
  );
};

export const useDeleteRole = () => {
  const { user } = useAuth();
  return useMutation(
    async (role: string) => {
      const res = await HttpService.delete(
        `${basePath(
          user.user_identification,
          user.entity_identification
        )}/${encodeURIComponent(role)}`
      );
      return res?.data?.element;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "roles-list",
          user?.entity_identification,
        ]);
      },
    }
  );
};
